import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Legend,
} from "recharts";
import { TrendingUp, Activity, Repeat, Zap } from "lucide-react";
import CompareBadge from "@/components/reports/CompareBadge";
import { useDrillThrough } from "@/hooks/useDrillThrough";

const COLORS = ["#4f46e5", "#059669", "#ea580c", "#9333ea", "#0ea5e9", "#e11d48", "#64748b", "#d97706"];
const HEAT_TINT = (v, max) => {
  if (max === 0) return "rgba(79,70,229,0)";
  return `rgba(79,70,229,${0.05 + 0.85 * (v / max)})`;
};

export default function VolumeTrendsReport({ filters, granularity, onGranularity }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const drillTo = useDrillThrough();

  useEffect(() => {
    setLoading(true);
    const params = { ...filters, granularity };
    Object.keys(params).forEach((k) => (params[k] === "all" || params[k] === "") && delete params[k]);
    api.get("/analytics/volume", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [JSON.stringify(filters), granularity]); // eslint-disable-line

  if (loading && !data) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;
  if (!data) return null;

  const heatMax = Math.max(...data.heatmap.flatMap((d) => d.hours));

  return (
    <div className="space-y-5" data-testid="volume-trends-report">
      {/* Top KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total tickets</div>
              <Activity className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-slate-900 flex items-baseline gap-2">
              {data.kpi.total}
              <CompareBadge pct={data.kpi.delta_pct} mode="positive-good" testid="volume-delta-badge" />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">vs previous equal-length period</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">New customers</div>
              <Repeat className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-emerald-700">
              {data.new_vs_repeat.find((x) => x.name.startsWith("New"))?.value || 0}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">unique customers</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Repeat customers</div>
              <Repeat className="w-4 h-4 text-amber-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-amber-700">
              {data.new_vs_repeat.find((x) => x.name.startsWith("Repeat"))?.value || 0}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">≥2 tickets in window</div>
          </CardContent>
        </Card>
      </div>

      {/* Granularity selector + Timeseries */}
      <Card className="border-slate-200" data-testid="volume-timeseries-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-base">Volume over time</CardTitle>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
            {["day", "week", "month"].map((g) => (
              <button
                key={g}
                onClick={() => onGranularity(g)}
                className={`px-3 py-1 ${granularity === g ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid={`granularity-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer>
            <BarChart data={data.timeseries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="new" stackId="a" fill="#059669" name="New customers" radius={[0, 0, 0, 0]}
                   cursor="pointer"
                   onClick={(d) => d?.bucket && drillTo("q", "", { date_from: d.bucket, date_to: d.bucket })} />
              <Bar dataKey="repeat" stackId="a" fill="#4f46e5" name="Repeat customers" radius={[6, 6, 0, 0]}
                   cursor="pointer"
                   onClick={(d) => d?.bucket && drillTo("q", "", { date_from: d.bucket, date_to: d.bucket })} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Heatmap */}
        <Card className="border-slate-200" data-testid="volume-heatmap-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Hour × Day heatmap</CardTitle></CardHeader>
          <CardContent className="p-4 overflow-x-auto">
            <table className="text-[10px] w-full">
              <thead>
                <tr>
                  <th className="text-slate-400 font-normal text-left pl-1"></th>
                  {Array.from({ length: 24 }).map((_, h) => (
                    <th key={h} className="text-slate-400 font-normal w-5">{h % 3 === 0 ? h : ""}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.heatmap.map((row) => (
                  <tr key={row.day}>
                    <td className="text-slate-500 font-medium pr-2">{row.day}</td>
                    {row.hours.map((v, h) => (
                      <td key={h} title={`${row.day} ${h}:00 — ${v} ticket(s)`}>
                        <div
                          className="w-5 h-5 rounded-sm border border-slate-100 m-px"
                          style={{ backgroundColor: HEAT_TINT(v, heatMax) }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-400 mt-2">Darker = more tickets. Drives staffing decisions.</p>
          </CardContent>
        </Card>

        {/* Channel evolution */}
        <Card className="border-slate-200" data-testid="channel-evolution-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Channel mix over time</CardTitle></CardHeader>
          <CardContent className="h-64">
            {data.channels.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">No data</div>
            ) : (
              <ResponsiveContainer>
                <AreaChart data={data.channel_evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {data.channels.map((c, i) => (
                    <Area key={c} type="monotone" dataKey={c} stackId="1"
                          stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Spikes */}
      <Card className="border-slate-200" data-testid="volume-spikes-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-amber-500" /> Top inbound spikes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.top_spikes.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 text-center">No statistically significant spikes in this window.</div>
          ) : (
            <div className="space-y-2">
              {data.top_spikes.map((s) => (
                <button key={s.bucket} type="button"
                        onClick={() => drillTo("q", "", { date_from: s.bucket, date_to: s.bucket })}
                        className="w-full flex items-center justify-between text-sm hover:bg-amber-50/50 rounded px-2 py-1 transition-colors"
                        data-testid={`spike-${s.bucket}`}
                        title="Open tickets created on this day">
                  <span className="font-mono text-slate-700">{s.bucket}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {s.count} tickets
                    </Badge>
                    <span className="text-[11px] text-slate-400">expected ~{s.expected}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
